// src/encounter/lab-add-dialog-v3.tsx
// The Add lab popup on the agreed lab model (PROD-2849), canvas boards 6-9. `LabAddDialog`
// (lab-add-dialog.tsx) renders it when Scrips.Master's order-search answers here, and the current popup
// otherwise (see services/use-lab-order-search.ts).
//
// Top to bottom, as the boards draw it:
//   title "Add lab" · the search field (and its results: the clinic's rows, or "Your clinic: No match …"
//   then the Scrips standard list, board 9) · LABS + the type chip + the lab chip · the name and code line
//   · the specimen and prep chips · DIAGNOSIS * · TESTS (Panel: fixed; clinic panel: ticks follow
//   tickedByDefault, already-ordered tests dimmed and not added again) and the line under it ·
//   QUESTIONS FROM THE LAB · the "Sent by the clinic to NRL …" line · NOTE TO LAB / PATIENT · Save, Cancel.
// No Bill to and no prices: a contracted lab means the clinic bills, Patient's choice means it doesn't,
// and physicians never see a cost.
import { useMemo, useState } from 'react';
import { Button, type CodeSearchResult } from '@scripsteam/scrips-signal-ds';
import { OrderDialogShell } from './order-dialog-shell';
import type { DiagnosisOption } from './diagnosis-multi-select';
import {
  LabChip,
  LabChips,
  LabInfoLine,
  LabQuestions,
  LabSearchField,
  LabSearchResults,
  LabTypeChip,
  LabV3Tests,
} from './lab-add-v3-parts';
import {
  alreadyOrderedKeys,
  buildRows,
  chipRow,
  codeLine,
  counts,
  defaultPick,
  infoLine,
  initialTicks,
  isClinicPanelType,
  isSingleTestType,
  labChipText,
  labChipTitle,
  labMenu,
  questionsFor,
  testsHeader,
  testsHint,
  typeLabel,
  typeTone,
  unansweredRequired,
  type LabPick,
} from './lib/lab-order-v3';
import { toLabOrderV3 } from './lib/to-lab-order-v3';
import type { OtherLabOnPlan } from './lib/lab-catalogue';
import type { LabAoeAnswer, LabOrderSearchRef, LabVendorRef, OrderCoding } from './lib/order-payload';
import type { LabConfirm } from './lab-add-dialog';
import {
  orderSearchItemOf,
  useLabOrderItem,
  useLabOrderSearch,
  type LabOrderItemDetail,
  type LabOrderSearchItem,
} from './services/use-lab-order-search';

export interface LabAddDialogV3Props {
  diagnoses: DiagnosisOption[];
  initialDxIds?: readonly string[];
  idSuffix?: string | number;
  /**
   * A lab dragged or added from the A&P panel. One carrying its order-search row (PROD-2931) opens that
   * item by id; any other is searched by name and opened when one row matches.
   */
  initialItem?: CodeSearchResult;
  initialSubTests?: OrderCoding[];
  initialNote?: string;
  initialVendor?: LabVendorRef;
  /** Editing an order placed from this popup in this session. */
  initialOrderSearch?: LabOrderSearchRef;
  initialAoeAnswers?: LabAoeAnswer[];
  otherLabs?: readonly OtherLabOnPlan[];
  editing?: boolean;
  onConfirm: (order: LabConfirm) => void;
  onDelete?: () => void;
  onCancel: () => void;
}

const EMPTY_BOX = 'rounded-md border border-solid border-border-subtle px-3 py-6 text-center';
const HINT_ERROR = 'm-0 text-size-xs text-status-error';

const same = (a?: string | null, b?: string | null) =>
  (a ?? '').trim().toLowerCase() === (b ?? '').trim().toLowerCase();

/** The search row a dragged lab names: same name, or same code. */
function matchSeed(items: readonly LabOrderSearchItem[], seed: CodeSearchResult): LabOrderSearchItem | undefined {
  return (
    items.find((i) => !i.fromStandardList && same(i.name, seed.display)) ??
    items.find((i) => !i.fromStandardList && !!seed.code && (same(i.clinicCode, seed.code) || same(i.code, seed.code)))
  );
}

/** The lab an edit reopens on: the saved lab when it is still allowed, Patient's choice when saved so. */
function savedPick(detail: LabOrderItemDetail, ref?: LabOrderSearchRef, vendor?: LabVendorRef): LabPick | null {
  if (ref?.patientsChoice) return { kind: 'patients-choice' };
  const lab = vendor ? (detail.allowedLabs ?? []).find((l) => l.labVendorId === vendor.id) : undefined;
  return lab ? { kind: 'lab', lab } : null;
}

export function LabAddDialogV3({
  diagnoses,
  initialDxIds,
  idSuffix = '0',
  initialItem,
  initialSubTests,
  initialNote,
  initialVendor,
  initialOrderSearch,
  initialAoeAnswers,
  otherLabs,
  editing,
  onConfirm,
  onDelete,
  onCancel,
}: LabAddDialogV3Props) {
  const editRef = editing ? initialOrderSearch : undefined;
  // PROD-2931: a lab picked in the A&P rail / Add Order drawer from the order search carries its row,
  // so the popup opens that exact item by id. Any other seed is looked up by name below (matchSeed).
  const [seedItem] = useState<LabOrderSearchItem | null>(() => (editRef ? null : orderSearchItemOf(initialItem)));
  const [query, setQuery] = useState(() => (editRef ? '' : (initialItem?.display ?? '')));
  const [dxIds, setDxIds] = useState<string[]>(() => [...(initialDxIds ?? [])]);
  const [note, setNote] = useState(initialNote ?? '');
  const [itemId, setItemId] = useState<string | null>(editRef?.itemId ?? seedItem?.id ?? null);
  const [picked, setPicked] = useState<LabOrderSearchItem | null>(seedItem);
  // The query the current item was picked with: the results stay hidden until the physician types again.
  const [pickedQuery, setPickedQuery] = useState<string | null>(
    editRef ? '' : seedItem ? (initialItem?.display ?? '') : null,
  );
  const [seedTried, setSeedTried] = useState(!initialItem || !!editRef || !!seedItem);
  const [labPick, setLabPick] = useState<LabPick | null>(null);
  const [ticks, setTicks] = useState<Set<string>>(() => new Set());
  const [seededFor, setSeededFor] = useState<string | null>(null);
  const [answers, setAnswers] = useState<Record<string, string>>(() =>
    Object.fromEntries((initialAoeAnswers ?? []).map((a) => [(a.questionCode?.trim() || a.questionText.trim()).toLowerCase(), a.value])),
  );

  const q = query.trim();
  const showResults = pickedQuery === null || q !== pickedQuery.trim();
  const search = useLabOrderSearch(query, showResults);
  const item = useLabOrderItem(itemId);
  const detail = item.data && item.data.id === itemId ? item.data : null;

  // A dragged lab: once its search answers, open the one row that names it (render-time state update,
  // guarded so it runs once). No match leaves the search showing, filled in.
  if (!seedTried && initialItem && search.data) {
    setSeedTried(true);
    const hit = matchSeed(search.data.items, initialItem);
    if (hit) {
      setItemId(hit.id);
      setPicked(hit);
      setPickedQuery(query);
    }
  }

  // Seed the ticks once per item (render-time, like the legacy popup's select-all seed).
  if (detail && seededFor !== detail.id) {
    setSeededFor(detail.id);
    setTicks(initialTicks(detail, editRef?.itemId === detail.id ? initialSubTests : undefined));
  }

  const head: LabOrderSearchItem | null = detail ?? picked;
  const already = useMemo(() => alreadyOrderedKeys(detail, otherLabs), [detail, otherLabs]);
  const pick: LabPick | null = head
    ? (labPick ?? (detail && editRef?.itemId === detail.id ? savedPick(detail, editRef, initialVendor) : null) ?? defaultPick(head))
    : null;
  const c = detail ? counts(detail, ticks, already) : null;
  const questions = detail && pick ? questionsFor(detail, ticks, already, pick) : [];
  const missingAnswers = unansweredRequired(questions, answers);
  const clinic = isClinicPanelType(head?.type);
  const noneTicked = !!detail && clinic && !!c && c.ticked === 0;
  const canSave = !!detail && !!pick && !noneTicked && missingAnswers === 0;

  function pickItem(i: LabOrderSearchItem) {
    setItemId(i.id);
    setPicked(i);
    setPickedQuery(query);
    setLabPick(null);
    setSeededFor(null);
  }

  function toggle(key: string) {
    setTicks((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  function handleSave() {
    if (!detail || !pick || !canSave) return;
    const result = toLabOrderV3({
      detail,
      pick,
      ticked: ticks,
      already,
      questions,
      answers,
      note,
      orderId: `lab-${detail.clinicCode || detail.id}-${idSuffix}`,
      savedVendor: editing ? initialVendor : undefined,
    });
    onConfirm({ ...result, dxIds });
  }

  // ── search slot ──
  const searching = showResults && q.length >= 2;
  const noResults = searching && !search.isFetching && !!search.data && search.data.items.length === 0;
  const searchSlot =
    initialItem && itemId ? undefined : editRef ? undefined : (
      <div className="flex flex-col gap-2">
        <LabSearchField value={query} onChange={setQuery} />
        {searching && search.isFetching && (
          <div role="status" aria-live="polite" className="px-3 text-size-eyebrow text-text-muted">
            Searching…
          </div>
        )}
        {searching && search.isError && !search.isFetching && (
          <div className="flex items-center justify-between">
            <p className={HINT_ERROR}>Couldn&apos;t search lab tests</p>
            <Button variant="tertiary" size="sm" onClick={() => void search.refetch()}>
              Retry
            </Button>
          </div>
        )}
        {searching && search.data && !search.isFetching && <LabSearchResults result={search.data} onPick={pickItem} />}
      </div>
    );
  const emptyBox =
    noResults && !head ? (
      <div className={EMPTY_BOX} data-testid="lab-no-results">
        <div className="text-size-sm font-semibold text-text-primary">{`No lab tests match “${q}”`}</div>
        <div className="mt-1 text-size-sm leading-meta text-text-muted">
          Search by name, lab code or LOINC. Only tests your clinic can order are listed.
        </div>
      </div>
    ) : undefined;

  // ── header ──
  const menu = head ? labMenu(head) : null;
  const chips = head && pick ? chipRow(head, pick.kind === 'patients-choice') : [];
  const info = detail && pick ? infoLine(detail, pick, c) : null;
  const rows = detail && !isSingleTestType(detail.type) ? buildRows(detail, ticks, already) : null;

  return (
    <OrderDialogShell
      title={editing ? 'Edit lab' : 'Add lab'}
      showTitle
      fieldLabel="Labs"
      itemName={head?.name ?? (editing ? (initialItem?.display ?? 'Loading…') : 'No lab selected')}
      itemCode={head ? codeLine(head) || undefined : undefined}
      emptySlot={emptyBox}
      headerAside={
        head && pick ? (
          <span className="inline-flex items-center gap-2">
            <LabTypeChip label={typeLabel(head.type)} tone={typeTone(head.type)} />
            <LabChip
              text={labChipText(detail ?? head, pick)}
              title={labChipTitle(detail ?? head, pick)}
              labs={menu}
              selectedLabId={pick.kind === 'lab' ? pick.lab.labVendorId : null}
              onPickLab={(lab) => setLabPick({ kind: 'lab', lab })}
              onPickPatientsChoice={() => setLabPick({ kind: 'patients-choice' })}
            />
          </span>
        ) : undefined
      }
      subHeader={chips.length > 0 ? <LabChips chips={chips} /> : undefined}
      diagnosisVariant="select-all"
      diagnoses={diagnoses}
      selectedDxIds={dxIds}
      onDxChange={setDxIds}
      note={note}
      onNoteChange={setNote}
      canSave={canSave}
      onSave={handleSave}
      onCancel={onCancel}
      onDelete={onDelete}
      searchSlot={searchSlot}
    >
      {itemId && item.isError && (
        <div className="flex items-center justify-between">
          <p className={HINT_ERROR}>Couldn&apos;t load this lab&apos;s tests</p>
          <Button variant="tertiary" size="sm" onClick={() => void item.refetch()}>
            Retry
          </Button>
        </div>
      )}
      {itemId && item.isLoading && (
        <div role="status" aria-live="polite" className="text-size-eyebrow text-text-muted">
          Loading the tests…
        </div>
      )}
      {rows && c && detail && (
        <LabV3Tests
          header={testsHeader(c)}
          rows={rows}
          onToggle={(key) => {
            if (rows.some((r) => r.key === key && r.state === 'tick')) toggle(key);
          }}
          hint={testsHint(detail, c)}
          error={noneTicked ? 'Select at least one test' : null}
        />
      )}
      <LabQuestions
        questions={questions}
        answers={answers}
        onAnswer={(key, value) => setAnswers((prev) => ({ ...prev, [key]: value }))}
      />
      {info && <LabInfoLine line={info} />}
    </OrderDialogShell>
  );
}

